import { FormEvent, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Page,
  WarningPanel,
} from '@backstage/core-components';
import { usePost } from './usePost';

type TokenRecord = {
  tokenSha256: string;
  requester: string;
  entityRef: string;
  tenantId: string | null;
  userId: string | null;
  environment: string;
  issuedAt: string;
  expiresAt: string;
};

const TestJwtLayout = ({
  subtitle,
  children,
}: {
  subtitle: string;
  children: React.ReactNode;
}) => (
  <Page themeId="tool">
    <Header title="Test JWT (eu-dev)" subtitle={subtitle} />
    <Content>
      <Grid container spacing={3} direction="column">
        {children}
      </Grid>
    </Content>
  </Page>
);

export const TestJwtLookupPage = () => {
  const post = usePost();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [record, setRecord] = useState<TokenRecord>();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(undefined);
    setRecord(undefined);
    try {
      setRecord(await post<TokenRecord>('/lookup', { token: input }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TestJwtLayout subtitle="Who requested this token? (tokens for dev-lifecycle APIs only)">
      <Grid item>
        <InfoCard title="Look up a token">
          <form onSubmit={onSubmit}>
            <TextField
              id="test-jwt-lookup-input"
              label="Token or sha256 hash"
              value={input}
              onChange={e => setInput(e.target.value)}
              required
              fullWidth
              multiline
              minRows={3}
              autoComplete="off"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              style={{ marginTop: 16 }}
            >
              Look up
            </Button>
          </form>
        </InfoCard>
      </Grid>
      {error && (
        <Grid item>
          <WarningPanel severity="warning" title="Lookup failed">
            {error}
          </WarningPanel>
        </Grid>
      )}
      {record && (
        <Grid item>
          <InfoCard title="Token record">
            <Typography>Requester: {record.requester}</Typography>
            <Typography>API: {record.entityRef}</Typography>
            <Typography>Tenant ID: {record.tenantId}</Typography>
            <Typography>User ID: {record.userId}</Typography>
            <Typography>Environment: {record.environment}</Typography>
            <Typography>
              Issued: {new Date(record.issuedAt).toLocaleString()}
            </Typography>
            <Typography>
              Expires: {new Date(record.expiresAt).toLocaleString()}
            </Typography>
            <Typography variant="caption">
              sha256: {record.tokenSha256}
            </Typography>
          </InfoCard>
        </Grid>
      )}
    </TestJwtLayout>
  );
};
